import { Space } from '@databases/types/confluence';
import { AnyAction } from '@reduxjs/toolkit';
import { ThunkAction } from 'redux-thunk';

import { getSpaceInfo, getSpaces, SpaceInfo } from '../../confluence/space';
import { asyncGeneratorToArray, serializeError } from '../../utils';
import { ContextState } from '../context/slice';
import { loadAsync } from '../redux';

import {
  selectSpaceInfo,
  selectSpaceInfoStatus,
  selectSpaces,
  selectSpacesStatus,
} from './selectors';
import spacesSlice, { SpacesState } from './slice';

export interface ContextAppState {
  spaces: SpacesState;
  context: ContextState;
}

type Dispatcher<Result> = ThunkAction<Result, ContextAppState, void, AnyAction>;

export const loadSpaceInfo =
  (spaceKey: string, { forceReload = false } = {}): Dispatcher<Promise<SpaceInfo>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectSpaceInfoStatus(getState(), { spaceKey }),
      () => {
        dispatch(spacesSlice.actions.loadSpaceInfoPending({ spaceKey }));
      },
      async () => await getSpaceInfo(spaceKey),
      async (spaceInfo) => {
        dispatch(spacesSlice.actions.loadSpaceInfoFinished(spaceInfo));
      },
      (err) => {
        dispatch(
          spacesSlice.actions.loadSpaceInfoError({ spaceKey, error: serializeError(err) })
        );
      },
      { forceReload }
    );

    return selectSpaceInfo(getState(), { spaceKey })!;
  };

export const loadSpaces =
  ({ forceReload = false } = {}): Dispatcher<Promise<Space[] | undefined>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectSpacesStatus(getState()),
      () => {
        dispatch(spacesSlice.actions.loadSpacesPending());
      },
      async () => await asyncGeneratorToArray(getSpaces()),
      async (spaceInfo) => {
        dispatch(spacesSlice.actions.loadSpacesFinished(spaceInfo));
      },
      (err) => {
        dispatch(spacesSlice.actions.loadSpacesError({ error: serializeError(err) }));
      },
      { forceReload }
    );

    return selectSpaces(getState());
  };
